import { defineStore } from 'pinia';
import { getCache, setCache } from '@/utils/cache';
import { get, patch, post, put } from '@/utils/request';
import axios from 'axios';
import { useRouter } from 'vue-router';
import cubejs from '@cubejs-client/core';
import usePlatformAnalytics from '@/composables/usePlatformAnalytics';
import { useSmeAuthStore } from './sme/sme-auth';
import { useSmeCompaniesStore } from './sme/companies';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    error: null as any,
    success: null as any,
    token: getCache('token'),
    isAdmin: getCache('isAdmin') || false,
    isLoggedIn: getCache('isLoggedIn') || false,
    profile: getCache('profile') || null,
    cubeToken: getCache('cubeToken'),
    checklist: getCache('checklist') as any,
    showDiscoveryInfoAlert: getCache('showDiscoveryInfoAlert') || 'show',
  }),

  getters: {
    isAuthenticated: state => state.isLoggedIn,
    isAnonymous: state => state.profile === null,
    isDisabled: state => state.profile && state.profile.account_disabled,
    isAgencyOwner(state) {
      if (!state.profile) return false;
      return (
        state.profile.companies.filter((e: any) => e.company_role === 'AGENCY').filter((e: any) => e.merchant_role === 'OWNER').length > 0
      );
    },
    isClusterOwner(state) {
      if (!state.profile) return false;
      return (
        state.profile.companies.filter((company: any) => {
          return company.cluster_role === 'ADMIN' || company.cluster_role === 'OWNER';
        }).length > 0
      );
    },
    isCompanyManager(state) {
      if (!state.profile) return false;
      return (
        state.profile.companies.filter((company: any) => {
          return company.cluster_role === 'COMPANY_MANAGER';
        }).length > 0
      );
    },
    isBrandManager(state) {
      if (!state.profile) return false;
      return (
        state.profile.companies.filter((company: any) => {
          return company.cluster_role === 'BRAND_MANAGER';
        }).length > 0
      );
    },
  },

  actions: {
    setError(value: any) {
      this.error = value;
    },
    clearToken() {
      // clear token
      this.profile = null;
      this.token = null;
      this.isAdmin = false;
      this.isLoggedIn = false;
      this.cubeToken = null;
      this.showDiscoveryInfoAlert = true; // Reset to true on logout
      setCache('profile', null);
      setCache('token', null);
      setCache('isAdmin', false);
      setCache('isLoggedIn', false);
      setCache('cubeToken', null);
      setCache('selectedCompany', null);
      setCache('showDiscoveryInfoAlert', true); // Reset to true on logout
      setCache('checklist', {
        billing_mode_added: false,
        brand_created: false,
        cluster_created: false,
        company_created: false,
        contract_created: false,
        email_verified: false,
        teammate_invited: false,
      });
    },

    toggleDiscoveryInfoAlert(action: 'show' | 'hide') {
      this.showDiscoveryInfoAlert = action;
      setCache('showDiscoveryInfoAlert', action);
    },

    async doSendVerificationEmail(form: any) {
      this.clearToken();
      this.error = null;
      try {
        await post('/hack/send_verification_email', form);
      } catch (e) {
        this.error = e;
      }
    },

    setToken(token: string) {
      this.token = token;
      this.isLoggedIn = true;

      setCache('token', token);
      setCache('isLoggedIn', true);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    },

    async doLogin({ email, password, router }: any) {
      this.clearToken();
      this.error = null;

      const smeAuth = useSmeAuthStore();
      const smeCompaniesStore = useSmeCompaniesStore();

      try {
        const { access_token, merchant_profile, is_sme } = await post('/auth/login/merchant', {
          username: email,
          password,
        });

        if (is_sme) {
          smeAuth.setSmeToken(access_token);

          await smeCompaniesStore.getCompanies();
          const smeCompany = smeCompaniesStore.companies.find(company => company.company_role === 'COMPANY');

          router.push(`/sme/${smeCompany?.id}/dashboard`);
        } else if (access_token) {
          this.setToken(access_token);
          // Set showDiscoveryInfoAlert to show for fresh login
          this.showDiscoveryInfoAlert = 'show';
          setCache('showDiscoveryInfoAlert', 'show');
        }
        return merchant_profile;
      } catch (e) {
        this.error = e;
      }
    },

    async doGetCubeToken() {
      const { access_token } = await get('/auth/cube');
      if (access_token) {
        this.cubeToken = access_token;
        setCache('cubeToken', this.cubeToken);
      }
    },

    doGenerateClientCube() {
      return cubejs(this.cubeToken, { apiUrl: import.meta.env.VITE_CUBEJS_DB_HOST || '' });
    },

    async doGetProfile() {
      try {
        const profile = await get('/user/merchant');
        this.profile = profile;
        setCache('profile', profile);
      } catch (e) {
        this.error = e;
      }
    },

    async doResendLink() {
      try {
        await get('/auth/resend_email_verification/merchant');
      } catch (e) {
        this.error = e;
        throw e;
      }
    },
    async doGetChecklist() {
      try {
        const checklist = await get('/user/merchant/onboarding/checklist');
        this.checklist = checklist;
        setCache('checklist', checklist);
      } catch (e) {
        const check = {
          billing_mode_added: false,
          brand_created: false,
          cluster_created: false,
          company_created: false,
          contract_created: false,
          email_verified: false,
          teammate_invited: false,
        };
        this.checklist = check;
        setCache('checklist', check);
        this.error = e;
      }
    },

    async doRequestPasswordReset({ email }: any) {
      this.clearToken();
      this.error = null;
      try {
        await post('/auth/reset_password/merchant', { email });
      } catch (e) {
        this.error = e;
      }
    },

    async doResetPassword({ email, password, token }: any) {
      this.clearToken();
      this.error = null;

      try {
        await post('/auth/save_password/merchant', { email, password, token });
      } catch (e) {
        this.error = e;
      }
    },

    async doCreateAccount(form: any) {
      this.error = null;
      try {
        await post('/hack/register', {
          ...form,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          company_name: form.company_name.trim(),
        });
      } catch (e) {
        this.error = e;
        useRouter;
      }
    },

    async doSignUpLogin(form: any) {
      this.error = null;
      try {
        const data = await post('/merchant/auth/initiate', {
          ...form,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          business_name: form.business_name.trim(),
        });
        return data;
      } catch (e: any) {
        this.error = e.message;
      }
    },

    async doCreateAccountFromInvite(form: any) {
      this.error = null;

      try {
        await post('/auth/create_account_from_invite/merchant', {
          ...form,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
        });
      } catch (e) {
        this.error = e;
      }
    },
    async doCreateNewCompany(form: any) {
      this.error = null;
      try {
        await post('/companies/new', form);
      } catch (e) {
        this.error = e;
      }
    },

    async doUpdateProfile(form: any) {
      const { trackEvent } = usePlatformAnalytics();
      const platformEventData: any = {
        event_name: 'ProfileUpdated',
        platform: 'adv',
        metadata: {
          user_id: this.profile.id,
        },
      };
      trackEvent(platformEventData);
      this.success = null;
      this.error = null;
      try {
        if (form.phone && form.phone !== this.profile.phone) {
          await patch('/user/merchant/phone_number', {
            phone_number: form.phone,
          });
        }

        this.profile = await put('/user/merchant', {
          ...form,
          accepted_terms_version: 'V1_MERCHANT_US_EN',
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
        });
        setCache('profile', this.profile);
      } catch (e) {
        this.error = e;
      }
    },

    async doLogout() {
      const { trackEvent } = usePlatformAnalytics();
      const platformEventData: any = {
        event_name: 'UserLogout',
        platform: 'adv',
        metadata: {
          user_id: this.profile.id,
        },
      };
      await trackEvent(platformEventData);
      this.clearToken();
    },
  },
});
